import { useState } from "react";
import { Form, Formik } from "formik";
import { object, string } from "yup";
import TextInput from "../UI/TextInput";
import FormikSubmitButton from "../UI/FormikSubmitButton";
import Button from "../UI/Button";
import useAuthenticatedAxios from "../../hooks/use-authenticated-axios";
import useAccount from "../../hooks/use-account";
import useTotpSecret from "../../hooks/use-totp-secret";
import Spinner from "../UI/Spinner";
import { handleAPIError, UNKNOWN_ERROR_MESSAGE } from "../../utils/index.jsx";
import Callout from "../UI/Callout.tsx";
import styles from "./TwoFactorAuthEnableForm.module.css";

export default function TwoFactorAuthEnableForm({ onClose }) {
  const axios = useAuthenticatedAxios();
  const { refresh: refreshAccount } = useAccount();
  const {
    totpSecret,
    isLoading: generating,
    error: generateError,
  } = useTotpSecret();
  const [backupCodes, setBackupCodes] = useState(null);
  const [error, setError] = useState("");

  async function enableHandler(values) {
    try {
      setError("");
      const response = await axios.post("/account/totp/enable", {
        secret: totpSecret.secret,
        code: values.code,
      });
      setBackupCodes(response.data.backup_codes);
    } catch (error) {
      setError(handleAPIError(error));
    }
  }

  if (backupCodes) {
    return (
      <div>
        <p className={"cardTitle"}>Codes de récupération</p>
        <p className={"marginT10"}>
          Conservez ces codes en lieu sûr. Ils vous permettront de récupérer
          l'accès à votre compte si vous perdez votre appareil
          d'authentification.
        </p>
        <ul
          className={"marginT20 primaryText"}
          style={{ listStyleType: "none", padding: 0 }}
        >
          {backupCodes.map((code, index) => (
            <li key={index}>{code}</li>
          ))}
        </ul>
        <div className={"text-right marginT20"}>
          <Button
            variant="secondary"
            onClick={async () => {
              await refreshAccount();
              onClose();
            }}
          >
            Fermer
          </Button>
        </div>
      </div>
    );
  }

  if (generating) {
    return (
      <div>
        <p className={"cardTitle"}>
          Activer l'authentification à deux facteurs
        </p>
        <div className={"marginT20 text-center"}>
          <Spinner />
        </div>
      </div>
    );
  }

  if (generateError) {
    return <Callout variant="danger">{UNKNOWN_ERROR_MESSAGE}</Callout>;
  }

  return (
    <div>
      <p className={"cardTitle"}>Activer l'authentification à deux facteurs</p>
      <p className={"primaryText marginT10"}>
        1. Scannez ce code QR avec votre application d'authentification ou
        entrez le code suivant :
      </p>
      <div className={"marginT20 text-center"}>
        <img
          src={`data:image/png;base64,${totpSecret.qr_code}`}
          alt="QR Code"
          style={{ maxWidth: "300px", margin: "0 auto" }}
        />
        <br />
        <p>{totpSecret.secret}</p>
      </div>
      <p className={"primaryText marginT20"}>
        2. Entrez le code à 6 chiffres pour activer
      </p>
      <Formik
        initialValues={{ code: "" }}
        validationSchema={object({
          code: string()
            .required("Code requis")
            .matches(/^\d{6}$/, "Code doit contenir 6 chiffres"),
        })}
        onSubmit={enableHandler}
      >
        <Form className={styles.form}>
          <TextInput name="code" label="Code de vérification" marginForm="" />
          <div className={styles.actions}>
            <Button variant="secondary" onClick={onClose}>
              Annuler
            </Button>
            <FormikSubmitButton>Activer</FormikSubmitButton>
          </div>
          <Callout variant="danger">{error}</Callout>
        </Form>
      </Formik>
    </div>
  );
}
